import {
  AfterViewInit,
  ChangeDetectorRef,
  Directive,
  ElementRef,
  EventEmitter,
  Host,
  Input,
  OnChanges,
  Optional,
  Output,
  Renderer2,
  Self,
  SimpleChanges,
} from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { map, startWith, Subscription } from 'rxjs';
import { ScreenResizeService } from '../services/screen-resize/screen-resize.service';

/**
 * Works from angular-material version 15. since all classes got the new prefix 'mdc-'
 */
@Directive({
  selector: '[appBubblePagination]',
})
export class BubblePaginationDirective implements AfterViewInit, OnChanges {
  /**
   * custom emitter for parent component
   */
  @Output() pageIndexChangeEmitter: EventEmitter<number> =
    new EventEmitter<number>();

  @Output() pageChangeWhileDisabled: EventEmitter<number> =
    new EventEmitter<number>();

  /**
   * whether we want to display first/last button and dots
   */
  @Input() showFirstButton = true;
  @Input() showLastButton = true;

  /**
   * how many buttons to display before and after
   * the selected button
   */
  @Input() renderButtonsNumber = 2;

  /**
   * how many elements are in the table
   */
  @Input() appCustomLength: number = 0;

  /**
   * how many pages are in the table
   */
  @Input() totalPages: number = 1;

  /**
   * set true to hide left and right arrows surrounding the bubbles
   */
  @Input() hideDefaultArrows = false;

  @Input() pageIndex: number = 0;

  @Input() disablePagination: boolean = false;

  /**
   * references to DOM elements
   */
  private dotsEndRef!: HTMLElement;
  private dotsStartRef!: HTMLElement;
  private bubbleContainerRef!: HTMLElement;

  // remember rendered buttons on UI that we can remove them when page index change
  private buttonsRef: HTMLElement[] = [];

  private lastPageIndex: number = 0;

  private screenWidth: number = window.innerWidth;
  private mobileWidth: number = 767;
  private subscription: Subscription = new Subscription();

  constructor(
    @Host() @Self() @Optional() private readonly matPag: MatPaginator,
    private elementRef: ElementRef,
    private ren: Renderer2,
    private screenResizeService: ScreenResizeService,
    private cdr: ChangeDetectorRef,
  ) {
    this.subscription = this.screenResizeService.screenWidth$.subscribe(
      (width) => {
        this.screenWidth = width;
        this.cdr.markForCheck();
        this.onPaginatorRender();
      },
    );
  }

  ngAfterViewInit(): void {
    this.onPaginatorRender();
  }

  /**
   * react on parent component changing the appCustomLength - rerender bubbles
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (!changes?.['appCustomLength']?.firstChange || changes['totalPages']) {
      this.removeButtons();
      this.switchPage(this.pageIndex);
      this.renderButtons();
    }
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  onPaginatorRender(): void {
    this.removeButtons();

    if (this.screenWidth > this.mobileWidth) {
      this.styleDefaultPagination();
      this.createBubbleDivRef();
      this.renderButtons();
    }
  }

  private renderButtons(): void {
    // build buttons to UI
    this.buildButtons();

    if (this.pageIndex > 0) {
      this.switchPage(this.pageIndex);
    }

    // when pagination change -> change button styles
    this.matPag.page
      .pipe(
        map((e) => [e.previousPageIndex ?? 0, e.pageIndex]),
        startWith([0, this.lastPageIndex]),
        // takeUntilDestroyed() // <-- does not work
      )
      .subscribe(([prev, curr]) => {
        this.lastPageIndex = curr;
        if (this.screenWidth > this.mobileWidth) {
          this.changeActiveButtonStyles(prev, curr);
        }
      });
  }

  /**
   * change the active button style to the current one and display/hide additional buttons
   * based on the navigated index
   */
  private changeActiveButtonStyles(previousIndex: number, newIndex: number) {
    // if (this.appCustomLength === 0) {
    //   this.buttonsRef = [this.createButton(0)];
    // }

    const previouslyActiveButtons =
      this.elementRef.nativeElement.querySelectorAll('.g-bubble__active');
    const currentActive = this.buttonsRef[newIndex];

    if (!currentActive) return;

    // Remove active style from previously active buttons
    previouslyActiveButtons.forEach((element: any) => {
      this.ren.removeClass(element, 'g-bubble__active');
    });
    // Add active style to new active button
    this.ren.addClass(currentActive, 'g-bubble__active');

    // Hide all buttons by default
    this.buttonsRef.forEach((button) =>
      this.ren.setStyle(button, 'display', 'none'),
    );
    this.ren.setStyle(this.dotsStartRef, 'display', 'none');
    this.ren.setStyle(this.dotsEndRef, 'display', 'none');

    // Always show the first and last buttons
    const firstButton = this.buttonsRef[0];
    const lastButton = this.buttonsRef[this.buttonsRef.length - 1];
    this.ren.setStyle(firstButton, 'display', 'flex');
    this.ren.setStyle(lastButton, 'display', 'flex');

    // Show buttons and dots depending on the current page and total pages
    if (this.buttonsRef.length <= 4) {
      // For 4 or fewer pages, show all buttons without dots
      this.buttonsRef.forEach((button) => {
        this.ren.setStyle(button, 'display', 'flex');
      });
    } else {
      // Handle cases with more than 4 pages
      if (newIndex === 0) {
        // On the first page: "1 ... 3 ... 5"
        this.ren.setStyle(this.dotsEndRef, 'display', 'block');
        this.ren.setStyle(this.buttonsRef[1], 'display', 'flex');
        // this.ren.setStyle(this.buttonsRef[2], 'display', 'flex');
      } else if (newIndex === this.buttonsRef.length - 1) {
        // On the last page: "1 ... 4 ... 5"
        this.ren.setStyle(this.dotsStartRef, 'display', 'block');
        this.ren.setStyle(
          this.buttonsRef[this.buttonsRef.length - 2],
          'display',
          'flex',
        );
        // this.ren.setStyle(
        //   this.buttonsRef[this.buttonsRef.length - 3],
        //   'display',
        //   'flex'
        // );
      } else if (newIndex === 1) {
        // On the second page: "1 2 3 ... 5"
        this.ren.setStyle(this.dotsEndRef, 'display', 'block');
        this.ren.setStyle(this.buttonsRef[1], 'display', 'flex');
        // this.ren.setStyle(this.buttonsRef[2], 'display', 'flex');
      } else if (newIndex === this.buttonsRef.length - 2) {
        // On the second-last page: "1 ... 4 5"
        this.ren.setStyle(this.dotsStartRef, 'display', 'block');
        this.ren.setStyle(
          this.buttonsRef[this.buttonsRef.length - 2],
          'display',
          'flex',
        );
        // this.ren.setStyle(
        //   this.buttonsRef[this.buttonsRef.length - 3],
        //   'display',
        //   'flex'
        // );
      } else {
        // For middle pages: "1 ... x ... 5"
        this.ren.setStyle(this.dotsStartRef, 'display', 'block');
        this.ren.setStyle(this.dotsEndRef, 'display', 'block');
        this.ren.setStyle(this.buttonsRef[newIndex], 'display', 'flex');
        // this.ren.setStyle(this.buttonsRef[newIndex - 1], 'display', 'flex');
        // this.ren.setStyle(this.buttonsRef[newIndex + 1], 'display', 'flex');
      }
    }
  }

  /**
   * Removes or change styling of some html elements
   */
  private styleDefaultPagination() {
    const nativeElement = this.elementRef.nativeElement;
    const itemsPerPage = nativeElement.querySelector(
      '.mat-mdc-paginator-page-size',
    );
    const itemsPerPageLabel = nativeElement.querySelector(
      '.mat-mdc-paginator-page-size-label',
    );
    const howManyDisplayedEl = nativeElement.querySelector(
      '.mat-mdc-paginator-range-label',
    );
    const previousButton = nativeElement.querySelector(
      'button.mat-mdc-paginator-navigation-previous',
    );
    const nextButtonDefault = nativeElement.querySelector(
      'button.mat-mdc-paginator-navigation-next',
    );

    const pageSizeOptions = nativeElement.querySelector(
      '.mat-mdc-paginator-page-size-select',
    );

    // remove 'items per page'
    // this.ren.setStyle(itemsPerPage, 'display', 'none');

    // style text of how many elements are currently displayed
    // this.ren.setStyle(howManyDisplayedEl, 'display', 'none');

    // this.ren.setProperty(howManyDisplayedEl, 'textContent', 'Itens por página');
    // this.ren.setProperty(itemsPerPageLabel, 'textContent', 'Itens por página');

    // check whether the user wants to remove left & right default arrow
    if (this.hideDefaultArrows) {
      this.ren.setStyle(previousButton, 'display', 'none');
      this.ren.setStyle(nextButtonDefault, 'display', 'none');
    }

    if (!pageSizeOptions) {
      const pageSizeValue = nativeElement.querySelector(
        '.mat-mdc-paginator-page-size-value',
      );

      pageSizeValue
        ? this.ren.setStyle(pageSizeValue, 'display', 'none')
        : null;
      itemsPerPageLabel
        ? this.ren.setStyle(itemsPerPageLabel, 'display', 'none')
        : null;

      itemsPerPage
        ? this.ren.removeClass(itemsPerPage, 'mat-mdc-paginator-page-size')
        : null;
    }
  }

  /**
   * creates `bubbleContainerRef` where all buttons will be rendered
   */
  private createBubbleDivRef(): void {
    const actionContainer = this.elementRef.nativeElement.querySelector(
      'div.mat-mdc-paginator-range-actions',
    );
    const nextButtonDefault = this.elementRef.nativeElement.querySelector(
      'button.mat-mdc-paginator-navigation-next',
    );

    // create a HTML element where all bubbles will be rendered
    this.bubbleContainerRef = this.ren.createElement('div') as HTMLElement;
    this.ren.addClass(this.bubbleContainerRef, 'g-bubble-container');

    // render element before the 'next button' is displayed
    this.ren.insertBefore(
      actionContainer,
      this.bubbleContainerRef,
      nextButtonDefault,
    );
  }

  /**
   * helper function that builds all button and add dots
   * between the first button, the rest and the last button
   *
   * end result: (1) .... (4) (5) (6) ... (25)
   */
  private buildButtons(): void {
    const neededButtons = Math.ceil(
      this.appCustomLength / this.matPag.pageSize,
    );

    // remove all dots (.....)
    const elements = this.elementRef.nativeElement.querySelectorAll(
      '.bubble-paginator-dots',
    );
    elements.forEach((element: any) => {
      this.ren.removeChild(element.parentNode, element);
    });

    // if have only one page, dont create an first page, because it will be duplicated
    if (neededButtons !== 1 && this.appCustomLength !== 0) {
      // create first button
      this.buttonsRef = [this.createButton(0)];
    }
    // add dots (....) to UI
    this.dotsStartRef = this.createDotsElement();

    // create all buttons needed for navigation (except the first & last one)
    for (let index = 1; index < neededButtons - 1; index++) {
      this.buttonsRef = [...this.buttonsRef, this.createButton(index)];
    }

    // add dots (....) to UI
    this.dotsEndRef = this.createDotsElement();

    // create last button to UI after the dots (....)
    this.buttonsRef = [
      ...this.buttonsRef,
      this.createButton(neededButtons - 1),
    ];
  }

  /**
   * Remove all buttons from DOM
   */
  private removeButtons(): void {
    this.buttonsRef.forEach((button) => {
      this.ren.removeChild(this.bubbleContainerRef, button);
    });

    // Empty state array
    this.buttonsRef.length = 0;
  }

  /**
   * create button HTML element
   */
  private createButton(index: number): HTMLElement {
    const bubbleButton = this.ren.createElement('div');
    const text = this.ren.createText(String(index + 1));

    // add class & text
    this.ren.addClass(bubbleButton, 'g-bubble');
    this.ren.setStyle(bubbleButton, 'margin-right', '8px');
    this.ren.appendChild(bubbleButton, text);

    // react on click
    this.ren.listen(bubbleButton, 'click', () => {
      this.switchPage(index, true);
    });

    // render on UI
    if (this.bubbleContainerRef) {
      this.ren.appendChild(this.bubbleContainerRef, bubbleButton);
    }

    // set style to hidden by default
    this.ren.setStyle(bubbleButton, 'display', 'none');

    return bubbleButton;
  }

  /**
   * helper function to create dots (....) on DOM indicating that there are
   * many more bubbles until the last one
   */
  private createDotsElement(): HTMLElement {
    const dotsEl = this.ren.createElement('span');
    const dotsText = this.ren.createText('...');

    // add class
    this.ren.setStyle(dotsEl, 'font-size', '18px');
    this.ren.setStyle(dotsEl, 'margin-right', '8px');
    this.ren.setStyle(dotsEl, 'padding-top', '6px');
    this.ren.setStyle(dotsEl, 'color', '#919191');
    this.ren.addClass(dotsEl, 'bubble-paginator-dots');

    // append text to element
    this.ren.appendChild(dotsEl, dotsText);

    // render dots to UI
    if (this.bubbleContainerRef) {
      this.ren.appendChild(this.bubbleContainerRef, dotsEl);
    }

    // set style none by default
    this.ren.setStyle(dotsEl, 'display', 'none');

    return dotsEl;
  }

  /**
   * Helper function to switch page
   */
  private switchPage(i: number, userClick: boolean = false): void {
    if (this.disablePagination && userClick) {
      return this.pageChangeWhileDisabled.emit();
    }

    const previousPageIndex = this.matPag.pageIndex;
    this.matPag.pageIndex = i;
    this.matPag['_emitPageEvent'](previousPageIndex);

    this.pageIndexChangeEmitter.emit(i);
  }
}
